import useCurrentProfile from '@map/hooks/useCurrentProfile';
import { filterMap } from '@shared/util/object';
import { useDispatch, useSelector } from 'react-redux';
import { Reducer } from 'redux';
import { PROFILE_FETCHED, actionCreators as profileActions, useUpdateProfile } from './profile';

/** Retired tours:
  undoButton: null,
  firstExperienceAddSkills: null,
  firstArrival: null,
  firstArrivalOnTimeline: null,
  trySkillSearch: null,
  trySkillSearchAgain: null,
  toggleSkillDescription: null,
  skillCount: null,
  firstExp: null,
  getCv: null,
  */

// active tours:
const initialState = {
  saveGoalToggle: null,
  firstTimeFeedback: null,
  firstCvExport: null,
  jobGap: null,
  educationGap: null,
  hobbyGap: null,
  assessmentProgress: null,
  enteredLifeXFlow: null,
  theoryOfChangeInitialPrompt: null,
  theoryOfChangeFinalPrompt: null,
  theoryOfChangeInitialRePrompt: null,
  theoryOfChangeFinalRePrompt: null,
} as const;

type ToursState = Record<keyof typeof initialState, string | null>;

export type TourName = keyof ToursState;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const COMPLETE_TOUR = 'COMPLETE_TOUR' as const;

const actionCreators = {
  completeTour: (tour: string) => ({ type: COMPLETE_TOUR, tour }),
};

type Action = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const isCurrentTour = (name: string): name is TourName => {
  return (initialState as Record<string, unknown>)[name] !== undefined;
};

type ProfileAction = ReturnType<typeof profileActions.profileFetched>;
const tours: Reducer<ToursState, Action | ProfileAction> = (state = initialState, action) => {
  switch (action.type) {
    case COMPLETE_TOUR:
      return { ...state, [action.tour]: new Date().toISOString() };
    case PROFILE_FETCHED: {
      const newState = {
        ...initialState,
        ...filterMap(action.data.tours, (entry) => (isCurrentTour(entry[0]) ? entry : null)),
      };
      return newState;
    }
    default:
      return state;
  }
};

export default tours;

type RootState = { tours: ToursState };

export const useTours = () => useSelector(({ tours }: RootState) => tours);

export const useTour = (name: TourName) => useSelector(({ tours }: RootState) => tours[name]);

export const useCompleteTour = () => {
  const dispatch = useDispatch();
  const tours = useTours();
  const profile = useCurrentProfile();
  const updateProfile = useUpdateProfile();
  return (name: TourName) => {
    if (!tours[name]) {
      dispatch(actionCreators.completeTour(name));
      const tourNames = Object.entries(profile.tours)
        .filter(([, value]) => value)
        .map(([key]) => key);
      updateProfile({ tours: [...tourNames, name] }, true);
    }
  };
};
